import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import config from '../config/env';
import { ThemeColors, ThemeConfig, ThemeMode, ThemeVariant, defaultTheme, paletteFor } from './theme/themePalettes';
import { clearSavedTheme, isThemeCustomised, markThemeCustomised, readInitialTheme, saveTheme, validateImportedTheme } from './theme/themeStorage';
import { applyThemeToDom } from './theme/applyThemeToDom';

export type { ThemeMode, ThemeVariant, ThemeColors, ThemeConfig } from './theme/themePalettes';

interface ThemeContextType {
  theme: ThemeConfig;
  isDark: boolean;
  setTheme: (theme: Partial<ThemeConfig>) => void;
  setThemeMode: (mode: ThemeMode) => void;
  setThemeVariant: (variant: ThemeVariant) => void;
  toggleTheme: () => void;
  applyCustomColors: (colors: Partial<ThemeColors>) => void;
  resetTheme: () => void;
  exportTheme: () => string;
  importTheme: (themeJson: string) => boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * The theme: mode (light, dark, or the system's), density, animations.
 * Persisted in localStorage; server-side defaults fill the blanks until the
 * user picks something. The palettes, the storage and the DOM writing live
 * in `contexts/theme/`.
 */
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig>(() => readInitialTheme().theme);
  const [isDark, setIsDark] = useState<boolean>(() => readInitialTheme().isDark);

  const applyDark = useCallback((dark: boolean) => {
    setIsDark(dark);
    setTheme((prev) => ({ ...prev, colors: paletteFor(dark) }));
  }, []);

  // An explicit mode decides; 'auto' is handled by the system-preference effect.
  useEffect(() => {
    if (theme.mode === 'dark') applyDark(true);
    else if (theme.mode === 'light') applyDark(false);
  }, [theme.mode, applyDark]);

  // 'auto' follows the system preference, live.
  useEffect(() => {
    const mediaQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (!mediaQuery) return;
    const follow = () => {
      if (theme.mode === 'auto' && typeof mediaQuery.matches !== 'undefined') applyDark(mediaQuery.matches);
    };
    follow();
    mediaQuery.addEventListener('change', follow);
    return () => mediaQuery.removeEventListener('change', follow);
  }, [theme.mode, applyDark]);

  // Reduced motion turns the animations off.
  useEffect(() => {
    const mediaQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (!mediaQuery) return;
    const follow = () => setTheme((prev) => ({ ...prev, reducedMotion: mediaQuery.matches, animations: !mediaQuery.matches }));
    follow();
    mediaQuery.addEventListener('change', follow);
    return () => mediaQuery.removeEventListener('change', follow);
  }, []);

  useEffect(() => {
    applyThemeToDom(theme, isDark);
    saveTheme(theme);
  }, [theme, isDark]);

  // Server-side defaults fill the blanks, unless the user already customised the theme.
  // Runs on mount and again when the auth context announces a fresh token.
  useEffect(() => {
    let cancelled = false;
    const applyServerDefaults = async () => {
      if (cancelled || isThemeCustomised()) return;
      try {
        const { fetchEffectiveOnce } = await import('../services/uiSettingsService');
        const effective = await fetchEffectiveOnce();
        if (cancelled || !effective) return;
        const s = effective.settings || {};
        setTheme((prev) => ({
          ...prev,
          mode: (s.theme_mode as ThemeMode) ?? prev.mode,
          variant: (s.variant as ThemeVariant) ?? prev.variant,
          animations: typeof s.animations === 'boolean' ? s.animations : prev.animations,
        }));
        if (s.theme_mode === 'light') applyDark(false);
        else if (s.theme_mode === 'dark') applyDark(true);
      } catch {
        /* silent: the built-in defaults stay */
      }
    };
    applyServerDefaults();
    const listener = () => applyServerDefaults();
    window.addEventListener('app:auth-ready', listener);
    return () => {
      cancelled = true;
      window.removeEventListener('app:auth-ready', listener);
    };
  }, [applyDark]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    markThemeCustomised();
    setTheme((prev) => ({ ...prev, mode }));
    if (mode === 'light') applyDark(false);
    else if (mode === 'dark') applyDark(true);
    if (config.dev.debug) console.log('[THEME DEBUG] Theme mode changed to:', mode);
  }, [applyDark]);

  const setThemeVariant = useCallback((variant: ThemeVariant) => {
    markThemeCustomised();
    setTheme((prev) => ({ ...prev, variant }));
  }, []);

  const toggleTheme = useCallback(() => setThemeMode(isDark ? 'light' : 'dark'), [isDark, setThemeMode]);

  const applyCustomColors = useCallback((customColors: Partial<ThemeColors>) => {
    markThemeCustomised();
    setTheme((prev) => ({ ...prev, colors: { ...prev.colors, ...customColors } }));
  }, []);

  const updateTheme = useCallback((partialTheme: Partial<ThemeConfig>) => {
    markThemeCustomised();
    setTheme((prev) => ({ ...prev, ...partialTheme }));
  }, []);

  const resetTheme = useCallback(() => {
    setTheme(defaultTheme);
    clearSavedTheme();
  }, []);

  const exportTheme = useCallback((): string => JSON.stringify(theme, null, 2), [theme]);

  const importTheme = useCallback((themeJson: string): boolean => {
    try {
      const validated = validateImportedTheme(JSON.parse(themeJson));
      if (!validated) {
        console.error('[THEME] Imported theme failed validation');
        return false;
      }
      setTheme(validated);
      return true;
    } catch (error) {
      console.error('[THEME] Invalid theme import:', error);
      return false;
    }
  }, []);

  const contextValue = useMemo<ThemeContextType>(() => ({
    theme, isDark, setTheme: updateTheme, setThemeMode, setThemeVariant, toggleTheme, applyCustomColors, resetTheme, exportTheme, importTheme,
  }), [theme, isDark, updateTheme, setThemeMode, setThemeVariant, toggleTheme, applyCustomColors, resetTheme, exportTheme, importTheme]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
